import React from "react";
import { Button } from "@/components/ui/button";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import { RefreshCw, Eye, EyeOff, AlertTriangle } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { FontSizeOption } from "../../../../types/display";
import Preview from "./Preview";

interface PreviewData {
  title?: string;
  description?: string;
  items?: Array<any>;
  columns?: Array<{ key: string; label: string }>;
  yahrzeits?: Array<{ name: string; father_name?: string }>;
  [key: string]: any;
}

interface PreviewSectionProps {
  previewData: PreviewData | null;
  isLoading: boolean;
  previewError: string | null;
  isCorsError: boolean;
  fetchPreviewData: () => void;
  showPreview: boolean;
  togglePreview: () => void;
  showDescriptions: boolean;
  fontSize: FontSizeOption;
  selectedSections: string[];
  displayOptions: {
    showTitle: boolean;
    showDescription: boolean;
    showItems: boolean;
    showColumns: boolean;
  };
}

const PreviewSection: React.FC<PreviewSectionProps> = ({
  previewData,
  isLoading,
  previewError,
  isCorsError,
  fetchPreviewData,
  showPreview,
  togglePreview,
  showDescriptions,
  fontSize,
  selectedSections,
  displayOptions
}) => {
  if (!showPreview) {
    return (
      <div className="flex justify-center items-center p-10">
        <Button variant="outline" onClick={togglePreview} className="flex items-center gap-2">
          <Eye className="h-4 w-4" />
          <span>הצג תצוגה מקדימה</span>
        </Button>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-synagogue-blue"></div>
      </div>
    );
  }

  if (previewError) {
    return (
      <div className="text-center p-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>שגיאה בטעינת התצוגה המקדימה</AlertTitle>
          <AlertDescription>{previewError}</AlertDescription>
        </Alert>
      </div>
    );
  }

  if (isCorsError) {
    return (
      <div className="text-center p-8">
        <Alert variant="destructive">
          <AlertTriangle className="h-4 w-4" />
          <AlertTitle>שגיאת CORS</AlertTitle>
          <AlertDescription>
            לא ניתן לגשת לנתונים מהשרת. יש לוודא שהשרת מאפשר גישה מכל המקורות (CORS).
          </AlertDescription>
        </Alert>
      </div>
    );
  }

  if (!previewData) {
    return (
      <div className="text-center p-8">
        <Button variant="outline" onClick={fetchPreviewData} className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          <span>טען תצוגה מקדימה</span>
        </Button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Preview
        data={previewData}
        showDescriptions={showDescriptions}
        fontSize={fontSize}
        selectedSections={selectedSections}
        displayOptions={displayOptions}
      />
    </div>
  );
};

export default PreviewSection;
